import { useState, useCallback, useMemo, useEffect } from 'react';
import styles from './StudyPlan.module.scss';
import { HTML5Backend } from 'react-dnd-html5-backend';
import { showSuccessNotification } from '@hooks/useNotification';
import {
  HeaUnitContentFragment,
  HeaUnit,
  useHeaListStudyPlansLazyQuery,
  useHeaUnitsLazyQuery,
  StudyPlan as StudyPlanType,
  useHeaCreateStudyPlanMutation,
  useHeaUpdateStudyPlanMutation,
  useHeaDeleteStudyPlanMutation,
  useHeaEnableFaithMutation,
  useHeaUpdateStudyPlanNameMutation,
  useHeaLessonsToConfirmLazyQuery
} from '@api/graphql-codegen';
import Spinner from 'src/components/spinner/Spinner';
import Header from '@src/components/header/HeaHeader';
import AddStudyPlan from './add-studyplan/AddStudyPlan';
import GridUnitsLayout from './gridLayout/GridUnitsLayout';
import TopicFilterLayout from './filterLayout/TopicFilterLayout';
import UnitProgressModal from '@src/components/unit-progress-modal/UnitProgressModal';
import DeleteModal from '@components/modal/delete-modal/DeleteModal';
import { Collapse } from "react-collapse";
import './customStyle.css';
import { useAnalytics, Events } from '@hooks';
import IconSelect from 'src/assets/icons/select_mark.svg?react';
// import IconUnselect from 'src/assets/icons/cross_mark.svg?react';
import IconPencil from 'src/assets/icons/pencil.svg?react';
import AvatarIcon from 'src/assets/icons/avatar.svg?react';
import { ModeTypeEnum } from '@types';
import { parentPlanName, ageOptions } from '@constants';
import { useAuthToken } from '@src/hooks/useAuthToken';
import { useHeaUser } from '@hooks/useHeaUser';
import TabLayout from './tabLayout/TabLayout';
import Button from '@src/components/button/Button';
import EditStudyPlan from './add-studyplan/EditStudyPlan';
import { useMediaQuery } from 'react-responsive';
import { TouchBackend } from 'react-dnd-touch-backend';
import {
  DndProvider,
  TouchTransition,
  MouseTransition,
  usePreview
} from 'react-dnd-multi-backend'
import { getMediaAbsolutePath } from '@services/File';
import { logger } from '../../../utils/logger';

const colors = ['#FFB3B3', '#CADEFC', '#9EE6CF', '#F0D9FF', '#DFF4F3', '#FFCFDF', '#FFDBA4', '#FFD9C0', '#DEECFF', '#CDF0EA', '#FFE9AE', '#C1EFFF'];
const darkColors = ['#FF7E7E', '#94BFFF', '#4ECCA4', '#BB7AE4', '#69D7D1', '#FC75A2', '#FFB648', '#FFA467', '#A4A8FF', '#69D7C4', '#FAC947', '#51D2FF'];

function checkRepeat(array: StudyPlanType[], el: string) {
  for(var i = 0 ; i < array.length; i++)
    if(array[i].color === el) return true;
  return false;
}

function getRandColor(studyplans: StudyPlanType[], array: string[]) {
  var rand = array[Math.floor(Math.random()*array.length)];
  if(!checkRepeat(studyplans, rand)) {
     return rand;
  }
  return getRandColor(studyplans, array);
}

const blankStudyPlan: StudyPlanType = {
  id: '',
  name: '',
};

function StudyPlan() {
  const [user] = useHeaUser();
  const { trackEvent } = useAnalytics();
  const [courses, setCourses] = useState<string[]>([]);
  const [showModal, setShowModal] = useState(false);
  const [showUnit, setShowUnit] = useState(false);
  const [selUnit, setSelUnit] = useState<HeaUnit | null>(null);
  const [studyPlans, setStudyPlans] = useState<StudyPlanType[]>([]);
  const [faithEnable, setFaithEnable] = useState(false);
  const [showStudyPlans, setShowStudyPlans] = useState(false);
  const [name, setName] = useState('')
  const [color, setColor] = useState<string | null>(null)
  const [studyPlanMode, setStudyPlanMode] = useState(ModeTypeEnum.Units)
  const [selChildStudy, setSelChildStudy] = useState<StudyPlanType | undefined>(undefined);
  const [isHovered, setIsHovered] = useState(false);
  const [removeConfirmation, setRemoveConfirmation] = useState(false);
  const [deletingStudyPlanId, setDeleteingStudyPlanId] = useState<string>();
  const [showEditModal, setShowEditModal] = useState(false);
  const [visibles, setVisibles] = useState<boolean[]>([])
  const [dndUnit, setDndUnit] = useState<string>();
  const [topPos, setTopPos] = useState(88);
  const [navHidden, setNavHidden] = useState(false);

  const isTablet = useMemo(() => {
    const userAgent = navigator.userAgent.toLowerCase();
    return /(ipad|tablet|(android(?!.*mobile))|(windows(?!.*phone)(.*touch))|kindle|playbook|silk|(puffin(?!.*(IP|AP|WP))))/.test(userAgent)
  }, []);

  const isMobile = useMediaQuery({maxWidth: 800});
  // const isPortrait = useMediaQuery({ query: '(orientation: portrait)' });

  const [heaLessonsToConfirm, {loading: completedLessonLoading, data, refetch}] = useHeaLessonsToConfirmLazyQuery({ fetchPolicy: 'network-only' })
  const [heaListStudyPlans, {data: studyplanData, loading: studyLoading}] = useHeaListStudyPlansLazyQuery({fetchPolicy: 'network-only'});
  const [updateHeaFaithState, { loading: inUpdatingFaith }] = useHeaEnableFaithMutation({fetchPolicy: 'network-only'});
  const [getheaUnits, {data: heaUnits, loading}] = useHeaUnitsLazyQuery({fetchPolicy: 'network-only'});
  const [deleteStudyPlan, { loading: isDeletingStudyPlan }] = useHeaDeleteStudyPlanMutation({
    onCompleted: () => {
      if (deletingStudyPlanId === selChildStudy?.id) {
        setSelChildStudy(undefined)
      }
    }
  });

  useEffect(() => {
    if (user?.enableFaith) {
      setFaithEnable(true);
    }
  }, [user?.enableFaith]);

  const { visitProfile, removeVisitProfile } = useAuthToken();

  useEffect(() => {
    if (studyPlans.length === 0) {
      setStudyPlanMode(ModeTypeEnum.Units);
      setShowStudyPlans(false)
    } else {
      const existBlankPlan = studyPlans.find(plan => plan.name === "")
      if (existBlankPlan) {
        setStudyPlanMode(ModeTypeEnum.Edit);
        setShowStudyPlans(true);
        setSelChildStudy(existBlankPlan)
        return
      }
      if (visitProfile) {
        setStudyPlanMode(ModeTypeEnum.Study);
        setShowStudyPlans(false);
        removeVisitProfile();
      }
    }
  },[visitProfile, studyPlans, removeVisitProfile])

  useEffect(() => {
    heaListStudyPlans();
  }, [heaListStudyPlans])

  useEffect(() => {
    getheaUnits();
  }, [getheaUnits])

  useEffect(() => {
    heaLessonsToConfirm()
  }, [heaLessonsToConfirm])

  const [heaCreateStudyPlan, {loading: creating}] = useHeaCreateStudyPlanMutation({
    onCompleted(data) {
      if (showModal) setShowModal(false);
      if (showEditModal) setShowEditModal(false);
      if (data.heaCreateStudyPlan) {
        const newColor = color ? color : getRandColor(studyPlans, colors)
        const foundIdx = colors.findIndex(el => el === newColor)
        const darkColor = darkColors[foundIdx]
        setStudyPlans((prev: any) => {
          var oldList = name === parentPlanName ? [...prev] : [...prev].filter(plan => plan.name !== "")
          const plan = {
            id: data.heaCreateStudyPlan,
            name: name,
            color: newColor,
            darkColor,
            unitIds: dndUnit ? [dndUnit] : (selUnit ? [selUnit.id] : [])
          }
          setSelChildStudy(plan);
          if(dndUnit) setDndUnit(undefined);
          return [...oldList, plan];
        })
        if (selUnit) {
          heaUpdateStudyPlan({
            variables: {
              input: {
                id: data.heaCreateStudyPlan,
                unitIds: [selUnit.id]
              }
            }
          });
        }
      }
    },
    onError(e) {
      if (showModal) setShowModal(false);
      if (showEditModal) setShowEditModal(false);
      logger.info('error', e.message)
    }
  })

  const [heaUpdateStudyPlan, {loading: updating}] = useHeaUpdateStudyPlanMutation({
    onCompleted(data) {
      if (data.heaUpdateStudyPlan) {
        showSuccessNotification('Study plan updated successfully!');
      }
    },
    update(_, __, { variables }) {
      if (variables?.input.id) {
        const index = studyPlans.findIndex(plan => plan.id === variables?.input.id);
        if (index > -1) {
          const plan = {
            id: variables?.input.id,
            name: studyPlans[index].name,
            color: studyPlans[index].color,
            darkColor: studyPlans[index].darkColor,
            unitIds: (variables?.input.unitIds || []) as string[],
          };
          const newStudyPlans = [...studyPlans];
          newStudyPlans[index].unitIds = (variables?.input.unitIds || []) as string[];
          setSelChildStudy(plan);
          setStudyPlans(newStudyPlans);
        }
      }
    },
    onError(e) {
      setShowModal(false);
      logger.info('error', e.message)
    }
  })

  const [heaUpdateStudyPlanName, {loading: updating1}] = useHeaUpdateStudyPlanNameMutation({
    onCompleted(data) {
      if (showEditModal) setShowEditModal(false);
      if (data.heaUpdateStudyPlanName) {
        showSuccessNotification('Study plan updated successfully!');
      }
    },
    update(_, __, { variables }) {
      if (variables?.input.id) {
        const index = studyPlans.findIndex(plan => plan.id === variables?.input.id);
        if (index > -1) {
          const plan = {
            id: variables?.input.id,
            name: variables.input.name,
            color: studyPlans[index].color,
            darkColor: studyPlans[index].darkColor,
            unitIds: studyPlans[index].unitIds,
          };
          const newStudyPlans = [...studyPlans];
          newStudyPlans[index].name = variables.input.name;
          setSelChildStudy(plan);
          setStudyPlans(newStudyPlans);
        }
      }
    },
    onError(e) {
      if (showEditModal) setShowEditModal(false);
      logger.info('error', e.message)
    }
  })

  const showAddNewStudyPlan = () => {
    setSelChildStudy(blankStudyPlan)
    setStudyPlanMode(ModeTypeEnum.Edit)
    setShowEditModal(true);
    setShowStudyPlans(true)
  }
  // Add new study plan
  const handleAddNewStudyPlan = useCallback((name: string) => {
    setName(name);
    const color = getRandColor(studyPlans, colors);
    setColor(color);
    const foundIdx = colors.findIndex(el => el === color);
    const darkColor = darkColors[foundIdx];

    heaCreateStudyPlan({
      variables: {
        input: {
          name,
          color,
          darkColor
        }
      }
    });
  },[heaCreateStudyPlan, studyPlans])

  // Update StudyPlan name
  const handleUpdateStudyPlan = useCallback((id: string, name: string, unitId?: string) => {
    setName(name);
    if (id === "") {
      const color = getRandColor(studyPlans, colors);
      setColor(color);
      const foundIdx = colors.findIndex(el => el === color);
      const darkColor = darkColors[foundIdx];

      heaCreateStudyPlan({
        variables: {
          input: {
            name,
            color,
            darkColor,
            unitId
          }
        }
      });
    } else {
      heaUpdateStudyPlanName({
        variables: {
          input: {
            id,
            name
          }
        }
      });
    }
  },[heaUpdateStudyPlanName, heaCreateStudyPlan, studyPlans])

  useEffect(() => {
    if (heaUnits?.heaUnits && studyplanData?.heaListStudyPlans && user?.id) {
      const parentUnitExist = heaUnits.heaUnits.find(unit => !unit.ageGroup?.includes('Parent & Caregiver'))
      const parentStudyExit = studyplanData?.heaListStudyPlans.find(plan => plan.name.includes('Parent & Caregiver'))

      if (parentUnitExist && !parentStudyExit) {
        handleAddNewStudyPlan(parentPlanName);
      }
    }
  }, [heaUnits?.heaUnits, studyplanData?.heaListStudyPlans, handleAddNewStudyPlan, user?.id])

  useEffect(() => {
    var plans: StudyPlanType[] = []
    if (studyplanData?.heaListStudyPlans) {
      if (studyplanData.heaListStudyPlans.length > 0) {
        plans = [ ...studyplanData?.heaListStudyPlans || []].map(plan => {
          const color = colors[Math.floor(Math.random() * colors.length)];
          const foundIdx = colors.findIndex(el => el === color)
          const darkColor = darkColors[foundIdx]
          if (!plan.color || !plan.darkColor) {
            return {
              ...plan,
              color: color,
              darkColor: darkColor
            }
          }
          return plan
        });

        if (plans.length === 1 && plans.findIndex(plan => plan.name.includes(parentPlanName)) > -1) {
          const color = getRandColor(plans, colors);
          const foundIdx = colors.findIndex(el => el === color);
          const darkColor = darkColors[foundIdx];
          const noNameStudyPlan = {
            ...blankStudyPlan,
            color: color,
            darkColor: darkColor
          }
          plans.push(noNameStudyPlan)
        }
      } else {
        const color = getRandColor(plans, colors);
        const foundIdx = colors.findIndex(el => el === color);
        const darkColor = darkColors[foundIdx];
        const noNameStudyPlan = {
          ...blankStudyPlan,
          color: color,
          darkColor: darkColor
        }
        plans.push(noNameStudyPlan)
      }
    }
    setStudyPlans(plans)
  }, [studyplanData?.heaListStudyPlans, setStudyPlans])

  const filtered = useMemo(() => {
    let filteredUnits: HeaUnit[] = heaUnits?.heaUnits || [];

    if (courses?.length) {
      filteredUnits = filteredUnits.filter(unit => {
        let currentTopics = [];
        try {
          currentTopics = unit.topics ? JSON.parse(unit.topics) : [];
        } catch {
          currentTopics = [];
        }
        if (!currentTopics?.length) {
          return false;
        }
        return !!currentTopics.some((topic) => courses.includes(topic));
      });
    }

    if (studyPlanMode === ModeTypeEnum.Study) {
      if (selChildStudy) {
        filteredUnits = filteredUnits.filter(unit => selChildStudy.unitIds?.includes(unit.id))
      }
    } else {
      if (studyPlanMode === ModeTypeEnum.Edit) {
        filteredUnits = filteredUnits.filter(unit => !unit.ageGroup?.includes('Parent & Caregiver'))
      }
    }
    return filteredUnits;
  }, [courses, heaUnits?.heaUnits, selChildStudy, studyPlanMode]);

  // Select child Study plan
  const handleSelChildStudy = useCallback((item: StudyPlanType) => {
    if (selChildStudy === item || studyPlanMode === ModeTypeEnum.Study) {
      showSuccessNotification('Study plan Updated');
      setSelChildStudy(undefined)
    } else {
      setSelChildStudy(item)
    }

  }, [selChildStudy, studyPlanMode])
  // Edit Study Plan
  const handleEditStudyPlan = (item: StudyPlanType) => {
    setStudyPlanMode(ModeTypeEnum.Edit)
    setSelChildStudy(item)
    setShowStudyPlans(true)
  }

  const handleStudyPlan = (item: StudyPlanType) => (e: any) => {
    e.stopPropagation()
    setSelChildStudy(item)
    setShowEditModal(true)
  }
  const handleSelectPlanMode = useCallback((planMode: ModeTypeEnum) => {
    setStudyPlanMode(planMode)
    if (planMode === ModeTypeEnum.Units || planMode === ModeTypeEnum.Edit) {
      setVisibles(ageOptions.map((group, i) => {
        if(i === 0) return true
        else return false
      }))
    }
    const existBlankPlan = studyPlans.find(plan => plan.name === "")
    setSelChildStudy(planMode === ModeTypeEnum.Edit ? existBlankPlan : undefined)
    setShowStudyPlans(planMode === ModeTypeEnum.Edit)
  }, [studyPlans]);

  const handleFaithContentEnable = useCallback(async (value: boolean) => {
    setFaithEnable(value);
    await updateHeaFaithState({ variables: { disable: !value } });
  }, [updateHeaFaithState]);

  const handleResetFilters = useCallback(() => {
    setCourses([]);
  }, []);

  const handleChangeOptions = useCallback((value: any) => {
    setCourses(value.filter(topic => topic !== "All"));
  }, []);

  const handleShowStudyPlans = useCallback(() => {
    setShowStudyPlans(!showStudyPlans);
  }, [showStudyPlans]);

  const handleOpenUnit = useCallback((studyPlan?: StudyPlanType) => (unit: HeaUnit) => {
    setSelChildStudy(studyPlan);
    if (unit) {
      setSelUnit(unit);
      setShowUnit(true);
      trackEvent(Events.actions.unitOpen, {
        study_plan_name: studyPlan?.name,
        unit_name: unit.name
      });
    }
  }, [trackEvent]);
  const handleCloseUnitModal = () => {
    setShowUnit(false);
    setSelUnit(null);
    setSelChildStudy(undefined);
    refetch();
  }

  const handleGoStudyPlan = useCallback((planId: string) => {
    const lessonForm = document.getElementById(planId);
    if (lessonForm) {
      lessonForm?.scrollIntoView({
        behavior: 'smooth',
        block: 'start'
      })
    }
  }, []);

  const handleMoveToStudyMode = useCallback((studyplan: StudyPlanType) => {
    setShowUnit(false);
    setStudyPlanMode(ModeTypeEnum.Study);
    setShowStudyPlans(false);
    setTimeout(() => {
      handleGoStudyPlan(studyplan.id)
    }, 500)
  }, [handleGoStudyPlan])
  // Remove Unit
  const handleRemoveUnit = useCallback((unit: HeaUnitContentFragment) => {
    var newUnits = [''], planId = ''
    setStudyPlans(currentPlans => currentPlans.map((plan: any, i: number) => {
      const exist = plan.unitIds?.find((item: any) => item === unit.id)
      if (exist && plan.id === selChildStudy?.id) {
        planId = plan.id
        newUnits = [...plan.unitIds || []].filter((item: any) => item !== unit.id)
        return {
          ...plan,
          unitIds: newUnits
        }
      }
      return plan
    }))

    heaUpdateStudyPlan({
      variables: {
        input: {
          id: planId,
          unitIds: newUnits
        }
      }
    });
  }, [setStudyPlans, selChildStudy, heaUpdateStudyPlan])

  // handle Drag and Drop on Right Side StudyPlan
  const handleDnDOnStudyPlan = useCallback((index: number, units: string[]) => {
    var needUpdate = false, newUnits = [''], planId = ''
    setStudyPlans(currentPlans => currentPlans.map((plan: any, i: number) => {
      if (plan.id === selChildStudy?.id) {
        planId = plan.id;
        newUnits = [...units]
        needUpdate = true;
        return {
          ...plan,
          unitIds: newUnits.reverse()
        }
      } else {
        return plan
      }
    }))
    if (needUpdate) {
      heaUpdateStudyPlan({
        variables: {
          input: {
            id: planId,
            unitIds: newUnits
          }
        }
      });
    }
  }, [selChildStudy?.id, setStudyPlans, heaUpdateStudyPlan]);
  // handle Drag and Drop
  const handleDrop = useCallback(
    (index: number, item: any) => {
      const dropUnit = item.unit as HeaUnitContentFragment
      if (!selChildStudy?.id) {
        if (index > 0) {
          setDndUnit(dropUnit.id)
          setShowEditModal(true)
        }
        return
      }
      try {
        var needUpdate = false, newUnits = [''], planId = ''
        var currentPlans = [...studyPlans];
        if (index === 0 && studyPlanMode === ModeTypeEnum.Edit) {
          currentPlans = currentPlans.map((plan: any, i: number) => {
            const exist = plan.unitIds?.find((item: any) => item === dropUnit.id)
            if (exist && plan.id === selChildStudy?.id) {
              needUpdate = true;
              planId = plan.id;
              newUnits = [...plan.unitIds || []].filter((item: any) => item !== dropUnit.id)
              return {
                ...plan,
                unitIds: newUnits
              }
            }
            return plan
          })
          setStudyPlans(currentPlans);
        } else {
          currentPlans = currentPlans.map((plan: any, i: number) => {
            if (plan.id === selChildStudy?.id) {
              const exist = plan.unitIds?.find((item: any) => item === dropUnit.id)
              if (exist) {
                return plan
              } else {
                planId = plan.id;
                newUnits = [...plan.unitIds || [], dropUnit.id]
                needUpdate = true;
                return {
                  ...plan,
                  unitIds: newUnits
                }
              }
            } else {
              return plan
            }
          })
          setStudyPlans(currentPlans);
          const parentExist = currentPlans.find(plan => plan.name.includes(parentPlanName));
          if (!parentExist) {
            handleAddNewStudyPlan(parentPlanName);
          }
        }
        if (needUpdate) {
          heaUpdateStudyPlan({
            variables: {
              input: {
                id: planId,
                unitIds: newUnits
              }
            }
          });
        }
      } catch (e) {
        logger.info(e);
      }
    }, [heaUpdateStudyPlan, studyPlans, selChildStudy, studyPlanMode, handleAddNewStudyPlan]
  );

  const unitsViewStyle = useMemo(() => {
    const classes = [styles.unitsView];
    if (!showStudyPlans) {
      classes.push(styles.leftPanelHidden);
    }
    return classes.join(' ');
  }, [showStudyPlans])

  const filteredStudyPlans = useMemo(() => {
    let currentPlans = [...studyPlans];

    const parentPlan = currentPlans.filter(plan => plan.name.includes(parentPlanName))
    if (studyPlanMode === ModeTypeEnum.Study) {
      const foundIdx = currentPlans.findIndex(plan => plan.name.includes(parentPlanName))
      currentPlans.push(currentPlans.splice(foundIdx, 1)[0]); // move Parent to bottom
      if (selChildStudy) {
        currentPlans = currentPlans.filter(plan => {
          return plan.id === selChildStudy?.id
        })
      }
    } else if (parentPlan.length > 0) {
      currentPlans = currentPlans.filter(plan => plan.id !== parentPlan[0].id)
    }
    return currentPlans
  }, [studyPlans, studyPlanMode, selChildStudy]);

  const leftViewDiv = (sp: StudyPlanType) => {
    if (sp.name.includes(parentPlanName)) {
      const getstartUnits = (heaUnits?.heaUnits || []).filter(unit => unit.ageGroup?.includes('Parent & Caregiver - Getting Started'))
      const resourceUnits = (heaUnits?.heaUnits || []).filter(unit => unit.ageGroup?.includes('Parent & Caregiver - Resources')).sort((a, b) => {
        return a.name.localeCompare(b.name)
      });
      return (<div key={sp.id}>
        {getstartUnits.length > 0 && <>
          <div className={`${styles.studyModeHeader} ${isTablet ? styles.headTabletStickyView : styles.headStickyView}`}>
            <div className={styles.studyModeTitleView}>
              <div className={styles.avatarContainer}><AvatarIcon /></div>
              <div className={styles.studyPlanTitle}>
                {'Parent & Caregiver - Getting Started'}
              </div>
            </div>
          </div>
          <GridUnitsLayout source={0} units={getstartUnits} studyMode={studyPlanMode} handleOpenUnit={handleOpenUnit(sp)}/>
        </>}
        {resourceUnits.length > 0 && <>
          <div className={`${styles.studyModeHeader} ${isTablet ? styles.headTabletStickyView : styles.headStickyView}`}>
            <div className={styles.studyModeTitleView}>
              <div className={styles.avatarContainer}><AvatarIcon /></div>
              <div className={styles.studyPlanTitle}>
                {'Parent & Caregiver - Resources'}
              </div>
            </div>
          </div>
          <GridUnitsLayout source={0} units={resourceUnits} studyMode={studyPlanMode} handleOpenUnit={handleOpenUnit(sp)}/>
        </>}
      </div>)
    }
    const units = (sp?.unitIds || []).map(unitId => {
      return (heaUnits?.heaUnits || []).find(unit => unit.id && unit.id === unitId) as HeaUnit
    }).reverse()
    return (<div id={sp.id} key={sp.id}>
      <div className={`${styles.studyModeHeader} ${studyPlanMode !== ModeTypeEnum.Study ? isTablet ? styles.headTabletStickyView : styles.headStickyView : ''}`}>
        <div className={styles.studyModeTitleView}>
          <div className={styles.subItem} style={{background: `${sp.darkColor}`}}>
            <div className={styles.subItemTitle}>{sp.name === "" ? "?" : sp.name.substring(0, 1).toLocaleUpperCase()}</div>
          </div>
          <div className={styles.studyPlanTitle}>
            {sp?.name.includes(parentPlanName) ? parentPlanName : sp?.name ? `${sp?.name}'s Study Plan` : `No New Study Plans`}
          </div>
        </div>
        {sp?.name && <div className={styles.editStudyPlan} onClick={() => handleEditStudyPlan(sp)}>
          <IconPencil fill='white' />
        </div>}
      </div>
      {units.length > 0 ?
        <GridUnitsLayout source={0} units={units} studyMode={studyPlanMode} handleDrop={handleDrop} handleOpenUnit={handleOpenUnit(sp)} confirmLessons={data?.heaLessonsToConfirm ?? []}/>
        : <div className={styles.unitsDivStudyMode}>
            {sp?.name ? <div className={styles.emptyStudyPlanView} onClick={() => handleEditStudyPlan(sp)}>
              <div className={styles.insideDiv}>
                <h2>{'No Units Allocated'}</h2>
                <span>{`+ Add Unit's to ${sp.name}'s`}<br/> Study Plan</span>
              </div>
            </div> : <div className={styles.blankStudyPlanView} onClick={showAddNewStudyPlan}>
              <div className={styles.insideDiv}>
                <h2>{'+'}</h2>
                <span>{`Create Your First`}<br/> Study Plan!</span>
              </div>
            </div>}
          </div>
      }
    </div>)
  }

  const topicOptions: string[] = useMemo(() => (heaUnits?.heaUnits || []).reduce((prev, unit) => {
    let currentTopics = [];
    try {
      currentTopics = unit.topics ? JSON.parse(unit.topics) : [];
    } catch {
      currentTopics = [];
    }
    let newArray = prev.concat(currentTopics);
    newArray = newArray.filter((item,index)=>{
      return (newArray.indexOf(item) === index)
    });
    return newArray
  }, []), [heaUnits?.heaUnits]);

  useEffect(() => {
    if (ageOptions) {
      setVisibles(ageOptions.map((group, i) => {
        if(i === 0) return true
        else return false
      }))
    }
  }, [])

  const handleScroll = useCallback(() => {
    const navMenuElement = document.getElementById("navMenuBar")
    setVisibles(ageOptions.map((group, i) => {
      const element = document.getElementById("header"+i)
      if (element) {
        const rect = element.getBoundingClientRect();
        if (i === 0) {
          return rect.top < 300
        }
        if (isTablet) {
          return rect.top < 10
        }
        const navBarRect = navMenuElement?.getBoundingClientRect();
        return rect.top >= (navBarRect ? navBarRect.height : 0) && rect.top < (navBarRect ? navBarRect.height : 0) + 10
      }
      return false
    }))
    const leftScrollView = document.getElementById("leftScrollView")
    if (leftScrollView) {
      const rect = leftScrollView.getBoundingClientRect();
      const navBarRect = navMenuElement?.getBoundingClientRect();
      if (rect.top === 0) {
        setNavHidden(false)
        setTopPos(88);
      } else if (navBarRect && navBarRect.height - (87 - rect.top) < 130) {
        setNavHidden(true)
        setTopPos(rect.top);
      } else {
        setNavHidden(false)
      }
    }
  }, [isTablet])

  const filteredGroup = useMemo(() => {
    const groups = ageOptions.filter((group, i) => {
      const filteredByAge = filtered.filter(unit => unit.ageGroup === group.value)
      if (filteredByAge.length > 0) {
        return true;
      } else {
        return false
      }
    })
    return groups
  }, [filtered])

  const unitContent = (
    <div className={unitsViewStyle}>
      {<div id={'leftScrollView'} className={studyPlanMode === ModeTypeEnum.Study ? styles.studyModeBackground : ''}>
        <TabLayout studyMode={studyPlanMode} handleSelectPlanMode={handleSelectPlanMode} topPos={topPos}/>
        {studyPlanMode !== ModeTypeEnum.Study ?
          filteredGroup.map((ageGroup, i) => {
            const filteredByAge = filtered.filter(unit => unit.ageGroup === ageGroup?.value)
            if (filteredByAge.length > 0) {
              return (
                <div key={`${ageGroup.value}_${i}`}>
                  <div id={'header' + i} className={`${styles.groupHead} ${isTablet ? styles.headTabletStickyView : styles.headStickyView}`}>
                    <div className={styles.courseTitle}>
                      {ageGroup?.label.includes('Parent & Caregiver') ? ageGroup?.label : `Ages ${ageGroup?.label}`}
                    </div>
                    {(studyPlanMode === ModeTypeEnum.Units || studyPlanMode === ModeTypeEnum.Edit) && visibles[i] && <TopicFilterLayout
                      faithEnable={faithEnable} topicOptions={topicOptions} topics={courses} handleChangeTopic={handleChangeOptions} handleResetFilters={handleResetFilters} handleFaithContentEnable={handleFaithContentEnable}
                      disabled={inUpdatingFaith}
                    />}
                  </div>
                  <GridUnitsLayout source={0} units={filteredByAge} studyMode={studyPlanMode} handleDrop={handleDrop} handleOpenUnit={handleOpenUnit(undefined)}/>
                </div>
              )
            } else {
              return null
            }
          }) : (selChildStudy ? leftViewDiv(selChildStudy) : filteredStudyPlans.map(sp => leftViewDiv(sp)))
        }</div>
      }
    </div>
  )

  const rightViewStyle = useMemo(() => {
    const classes = [styles.rightView];
    if (showStudyPlans) {
      classes.push(styles.slideShow);
    }
    return classes.join(' ');
  }, [showStudyPlans])

  const leftViewStyle = useMemo(() => {
    const classes = [styles.leftView];
    if (showStudyPlans) {
      classes.push(styles.slideMove);
    }
    return classes.join(' ');
  }, [showStudyPlans])

  const showStudyPlanModal = useCallback(() => {
    setShowModal(true);
  }, []);

  const studyPlansForSelectedUnit: StudyPlanType[] = useMemo(() => {
    if (!selUnit?.id) {
      return [];
    }
    return studyPlans.filter((plan) => (plan.unitIds || []).includes(selUnit?.id!));
  }, [selUnit?.id, studyPlans]);

  const studyPlansForSelectedUnitIds = useMemo(() => studyPlansForSelectedUnit.map(unit => unit.id), [studyPlansForSelectedUnit]);

  const addUnitToStudyPlan = useCallback(() => {
    showStudyPlanModal();
  }, [showStudyPlanModal]);

  const onAddUnitToStudyPlan = useCallback(async (studyPlanId: string) => {
    if (studyPlanId) {
      const selectedStudyPlan = studyPlans.find(plan => plan.id === studyPlanId);
      if (selectedStudyPlan && selUnit?.id) {
        await heaUpdateStudyPlan({
          variables: {
            input: {
              id: studyPlanId,
              unitIds: [...(selectedStudyPlan?.unitIds || []), selUnit?.id]
            }
          }
        });
        setShowModal(false);
      }
    }
  }, [heaUpdateStudyPlan, selUnit?.id, studyPlans]);

  const handleConfirmRemove = useCallback(async () => {
    if (deletingStudyPlanId) {
      await deleteStudyPlan({
        variables: {
          input: { id: deletingStudyPlanId }
        }
      });
      await heaListStudyPlans();
      setRemoveConfirmation(false);
    }
  }, [deleteStudyPlan, deletingStudyPlanId, heaListStudyPlans]);

  const handleCancel = useCallback(() => {
    setRemoveConfirmation(false);
  }, []);

  const removeStudyPlan = useCallback((studyPlanId: string) => {
    setShowEditModal(false)
    setDeleteingStudyPlanId(studyPlanId);
    setRemoveConfirmation(true);
  }, []);
  const removeStudyName = useMemo(() => {
    if (!!deletingStudyPlanId) {
       const removeStudy = studyPlans.filter(plan => plan.id === deletingStudyPlanId)
       return removeStudy.length > 0 ? removeStudy[0].name : ''
    }
    return ''
  },[deletingStudyPlanId, studyPlans])

  const MyPreview = () => {
    const preview = usePreview()
    if (!preview.display) {
      return null
    }
    const {item, style} = preview;
    const { unit } = item as any;
    style.zIndex = '2';
    style.opacity = '0.4';
    style.width = 'calc((72% - 40px)/3 - 20px)';
    style.borderRadius = '3px';
    style.backgroundSize = 'cover';
    style.backgroundImage = `url(${unit.coverImage ? getMediaAbsolutePath(unit.coverImage) : ''})`;
    style.boxShadow = '0px 10px 10px rgba(0, 0, 0, 0.05)';

    return (
      <div style={style}>
        <div className={styles.card}>{}</div>
      </div>
    )
  }

  const HTML5toTouch = {
    backends: [
      {
        id: "html5",
        backend: HTML5Backend,
        transition: MouseTransition
      },
      {
        id: "touch",
        backend: TouchBackend,
        options: { enableTouchEvents: true },
        preview: true,
        transition: TouchTransition,
        skipDispatchOnTransition: true
      }
    ]
  };

  return (
    <>
      <Header studyPlans={studyPlans} heaUnits={heaUnits?.heaUnits ?? []} handleShowStudyPlans={handleShowStudyPlans} isHidden={navHidden} confirmLessons={data?.heaLessonsToConfirm ?? []} refetch={refetch} loading={completedLessonLoading}/>
      <div className={styles.wrapper}>
        <DndProvider options={HTML5toTouch}>
          {studyLoading || loading ? <div className={styles.spinner}><Spinner /></div> : <>
          <div id="customunits" className={styles.page}>
            <div className={leftViewStyle} onScroll={handleScroll}>
              {unitContent}
            </div>
            <div className={rightViewStyle}>
              <div className={styles.planListTitle}>Edit Study Plans</div>
              {filteredStudyPlans.map((item, i) => {
                const studyPlanUnits = item.unitIds?.map(unitId => {
                  return heaUnits?.heaUnits?.find(unit => unit.id && unit.id === unitId) as HeaUnit
                }).reverse()
                return (
                  <div key={i}>
                    <div className={styles.planItemContainer}>
                      <div className={styles.planItem} style={{background: `${item.color}`}} onClick={() => handleSelChildStudy(item)}>
                        <div className={styles.subItem} style={{background: (item.id === selChildStudy?.id) ? (studyPlanMode === ModeTypeEnum.Study) && isHovered ? '#FF656E' : '#FFFFFF' : `${item.darkColor}`}} onMouseOver={() => setIsHovered(true)} onMouseLeave={() => setIsHovered(false)}>
                          {item.id === selChildStudy?.id ? <IconSelect style={{ color: `${item.darkColor}` }} /> :
                          <div className={styles.subItemTitle}>{item.name === "" ? "?" : item.name.includes(parentPlanName) ? 'PC' : item.name.substring(0, 2).toLocaleUpperCase()}</div>}
                        </div>
                        <div className={styles.planName} style={{color: `${item.darkColor}`}}>
                          {item.name === "" ? "No Name" : item.name.includes(parentPlanName) ? parentPlanName : `${item.name}`}
                          <div className={styles.editPlan} onClick={handleStudyPlan(item)}>
                            <IconPencil fill={`${item.darkColor}`} />
                          </div>
                        </div>
                        {(studyPlanMode === ModeTypeEnum.Edit && !isMobile) && <div className={[styles.dropdownToggle, selChildStudy?.id === item.id ? styles.dropdownToggleOpen : styles.dropdownToggleClose].join(' ')}/>}
                      </div>
                    </div>
                    <div className={styles.planUnitsView} style={{background: `${item.color}`}}>
                    <Collapse isOpened={(studyPlanMode === ModeTypeEnum.Edit && item.id === selChildStudy?.id)}>
                      <GridUnitsLayout source={i+1} units={studyPlanUnits} studyMode={studyPlanMode} handleDrop={handleDrop} handleRightArrowAction={handleRemoveUnit} handleDnDOnStudyPlan={handleDnDOnStudyPlan} />
                    </Collapse></div>
                  </div>
                )
              })}
              {studyPlanMode === ModeTypeEnum.Edit && <div className={styles.itemWrapper}>
                <div className={styles.separateView}></div>
                <Button className={styles.addStudyPlanButton} label='+ Add New Study Plan' size='small' onClick={showStudyPlanModal} />
              </div>}
            </div>
            <div className={styles.gradientView}></div>
          </div>
          <MyPreview />
          </>}
        </DndProvider>
      </div>
      {(showUnit && selUnit) && <UnitProgressModal open={showUnit} onClose={handleCloseUnitModal} isEditMode={studyPlanMode === ModeTypeEnum.Edit} unitId={selUnit?.id || ''} heaUnitData={selUnit} addUnitToStudyPlan={addUnitToStudyPlan} studyPlansForSelectedUnit={studyPlansForSelectedUnit} studyMode={studyPlanMode} currentStudyPlan={selChildStudy} gotoStudyMode={handleMoveToStudyMode} studyPlans={studyPlans} heaUnits={heaUnits?.heaUnits ?? []}/>}
      {showModal && <AddStudyPlan open={showModal} studyPlans={studyPlans} creating={creating || updating} onClose={() => setShowModal(false)} onCreateNew={handleAddNewStudyPlan} onAddUnitToStudyPlan={selUnit?.id ? onAddUnitToStudyPlan : undefined} studyPlansForSelectedUnitIds={studyPlansForSelectedUnitIds} />}
      {removeConfirmation ? <DeleteModal size='small' open={removeConfirmation} onClose={handleCancel} onDelete={handleConfirmRemove} message={`Are you sure you want to delete ${removeStudyName}'s Study Plan?`} loading={isDeletingStudyPlan || studyLoading} cancelLabel={'NO'} deleteLabel={'DELETE'} /> : null}
      {showEditModal && <EditStudyPlan open={showEditModal} studyPlan={selChildStudy} studyPlans={studyPlans} creating={creating || updating1} onClose={() => setShowEditModal(false)} onUpdateStudyPlan={handleUpdateStudyPlan} onRemoveStudyPlan={removeStudyPlan} unitId={dndUnit} />}
    </>
  );
}

export default StudyPlan;
